import React, { useState } from "react";
import { ButtonSubmit } from "../Form/ButtonSubmit";
import { Input } from "../Form/Input";
import { LayoutForm } from "../Form/LayoutForm";
import { Select } from "../Form/Select";
import {
  Container,
  ErrorMessage,
  FormWrapper,
  ResultadoWrapper,
  Resumo,
  ResumoFinal,
  Tabela,
} from "./styled";

const iof = 0.0082;
const TAXA_CARTAO = 3.79; // % ao mês

interface CalculatorComponentProps {
  iofDia: number;
  taxaPadrao: number;
  parcelas: Parcela[];
}

interface Parcela {
  name: string;
  value: string;
}

interface ParcelaCalculada {
  numero: number;
  dias: number;
  taxa: number;
  juros: number;
  calculoIof: number;
  totalIof: number;
  valorTotal: number;
}

export function CalculatorComponent({
  iofDia,
  taxaPadrao,
  parcelas,
}: CalculatorComponentProps) {
  const [pedido, setPedido] = useState<number | null>(null);
  const [pedidoTexto, setPedidoTexto] = useState("");
  const [desconto, setDesconto] = useState<number | null>(null);
  const [descontoTexto, setDescontoTexto] = useState("");
  const [parcela, setParcela] = useState<number | null>(null);
  const [prazoInicio, setPrazoInicio] = useState(30);
  const [calculoParcela, setCalculoParcela] = useState<ParcelaCalculada[]>([]);
  const [valorTotalVendor, setValorTotalVendor] = useState(0);
  const [, setValorCartao] = useState(0);
  const [erro, setErro] = useState("");

  const pedidoDesconto =
    pedido && desconto ? pedido - pedido * (desconto / 100) : 0;

  const handlePedidoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let valor = e.target.value.replace(/\D/g, "");
    if (!valor) {
      setPedido(null);
      setPedidoTexto("");
      return;
    }

    const valorNumerico = parseFloat(valor) / 100;
    setPedido(valorNumerico);
    setPedidoTexto(
      valorNumerico.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      })
    );
  };

  const handleDescontoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let valor = e.target.value.replace(/\D/g, "");
    if (!valor) {
      setDesconto(null);
      setDescontoTexto("");
      return;
    }

    const percentual = parseFloat(valor) / 100;
    setDesconto(percentual);
    setDescontoTexto(`${percentual.toFixed(2).replace(".", ",")}%`);
  };

  function calcular() {
    if (!pedido || !parcela || desconto === null) {
      setErro("Preencha todos os campos para calcular.");
      return;
    }

    setErro("");

    const pedidoDesconto = pedido - pedido * (desconto / 100);
    const valorDaParcela = pedidoDesconto / parcela;
    const calculoIofdia = (iofDia * pedidoDesconto) / 100;
    const diasMes = 30;

    let resultadoParcela: ParcelaCalculada[] = [];
    let valorVendor = 0;

    for (let index = 0; index < parcela; index++) {
      const dias = prazoInicio + diasMes * index;
      let taxa = (taxaPadrao / diasMes) * dias;
      let juros = valorDaParcela * (taxa / 100);
      let calculoParcela = valorDaParcela + juros;
      let calculoIof = (valorDaParcela * iof * dias) / 100;
      let valorTotal = calculoParcela + calculoIof;
      if (index === 0) valorTotal += calculoIofdia;
      valorVendor += valorTotal;

      resultadoParcela.push({
        numero: index + 1,
        dias,
        taxa,
        juros,
        calculoIof,
        valorTotal,
        totalIof: calculoIofdia,
      });
    }

    const valorFinalCartao =
      pedidoDesconto * Math.pow(1 + TAXA_CARTAO / 100, parcela);
    setValorCartao(valorFinalCartao);

    setCalculoParcela(resultadoParcela);
    setValorTotalVendor(valorVendor);
  }

  return (
    <Container>
      <FormWrapper>
        <LayoutForm>
          <Input
            label="Valor do pedido"
            value={pedidoTexto}
            onChange={handlePedidoChange}
            placeholder="R$ 0,00"
          />

          <Input
            label="Desconto (%)"
            value={descontoTexto}
            onChange={handleDescontoChange}
            placeholder="Ex: 5%"
          />

          <Select
            name="parcela"
            label="Parcelas"
            onChange={(e) => setParcela(Number(e.target.value))}
            data={[
              { name: "1 Parcela", value: "1" },
              { name: "2 Parcelas", value: "2" },
              { name: "3 Parcelas", value: "3" },
              { name: "4 Parcelas", value: "4" },
              { name: "5 Parcelas", value: "5" },
            ]}
          />

          <Select
            name="prazoInicio"
            label="Prazo inicial"
            onChange={(e) => setPrazoInicio(Number(e.target.value))}
            data={parcelas}
          />

          {erro && <ErrorMessage>{erro}</ErrorMessage>}

          <ButtonSubmit onClick={calcular} type="button">
            Calcular
          </ButtonSubmit>

          {pedidoDesconto > 0 && (
            <Resumo>
              <span>Valor com desconto:</span>
              <strong>
                {pedidoDesconto.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </strong>
            </Resumo>
          )}
        </LayoutForm>
      </FormWrapper>

      {calculoParcela.length > 0 && (
        <ResultadoWrapper>
          <h3>Resultado da Condição Vendor</h3>

          <Tabela>
            <thead>
              <tr>
                <th>Parcela</th>
                <th>Dias</th>
                <th>Taxa</th>
                <th>IOF</th>
                <th>IOF ao dia</th>
                <th>Valor total</th>
              </tr>
            </thead>
            <tbody>
              {calculoParcela.map((item) => (
                <tr key={item.numero}>
                  <td>{item.numero}</td>
                  <td>{item.dias}</td>
                  <td>{item.taxa.toFixed(2)}%</td>
                  <td>
                    {item.totalIof.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </td>
                  <td>
                    {item.calculoIof.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </td>

                  <td>
                    {item.valorTotal.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>

          <ResumoFinal>
            <div>
              <span>Total Vendor: </span>
              <strong>
                {valorTotalVendor.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </strong>
            </div>

            {/* <div>
              <span>Total Cartão (3,79%/mês): </span>
              <strong>
                {valorCartao.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </strong>
            </div>

            {valorCartao > valorTotalVendor && (
              <Economia
                positivo={valorCartao > valorTotalVendor}
              >{`Economia escolhendo Vendor: ${(
                valorCartao - valorTotalVendor
              ).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })}`}</Economia>
            )} */}
          </ResumoFinal>
        </ResultadoWrapper>
      )}
    </Container>
  );
}
